import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, TextField, InputAdornment, IconButton, Menu, MenuItem, Checkbox,
  ListItemText, TablePagination, useMediaQuery, useTheme, Autocomplete
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { it } from 'date-fns/locale';
import { Search, MoreVert } from '@mui/icons-material';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import axios from 'axios';

const ALL_COLUMNS = [
  { id: 'campaign_name', label: 'Campaign', default: true, mobile: true },
  { id: 'advertiser_name', label: 'Advertiser', default: true, mobile: false },
  { id: 'total_leads', label: 'Total', default: true, mobile: true },
  { id: 'positive_leads', label: 'Positive', default: true, mobile: true },
  { id: 'pending_leads', label: 'Pending', default: true, mobile: false },
  { id: 'trash_leads', label: 'Trash', default: true, mobile: false },
  { id: 'cpa_leads', label: 'CPA', default: true, mobile: false },
  { id: 'percent_positive', label: '% Pos', default: true, mobile: true },
  { id: 'percent_cpa', label: '% CPA', default: true, mobile: false },
  { id: 'avg_quality_score', label: 'Quality', default: true, mobile: false },
  { id: 'payout_per_lead', label: 'Payout', default: true, mobile: false },
  { id: 'total_revenue', label: 'Revenue', default: true, mobile: false },
  { id: 'total_spend', label: 'Cost', default: true, mobile: false },
  { id: 'profit', label: 'Profit', default: true, mobile: false },
  { id: 'impressions', label: 'Impr.', default: true, mobile: false },
  { id: 'clicks', label: 'Clicks', default: true, mobile: false },
  { id: 'ctr_link', label: 'CTR', default: true, mobile: false },
  { id: 'frequency', label: 'Freq.', default: true, mobile: false },
];

function FbAnalytics() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(true);
  const [campaignStats, setCampaignStats] = useState([]);
  const [advertisers, setAdvertisers] = useState([]);
  const [trend, setTrend] = useState([]);
  const [qualityTrend, setQualityTrend] = useState([]);
  const [cplTrend, setCplTrend] = useState([]);
  const [isHourly, setIsHourly] = useState(false); 
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedAdvertiser, setSelectedAdvertiser] = useState('all');
  const [anchorEl, setAnchorEl] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(ALL_COLUMNS.filter(c => c.default).map(c => c.id));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [period, setPeriod] = useState('month');
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);

  useEffect(() => { fetchData(); }, [period, customStart, customEnd, selectedAdvertiser, selectedCampaign]);
  useEffect(() => { setPage(0); }, [searchTerm, selectedAdvertiser, selectedCampaign]);

  const getDateRange = () => {
    const today = new Date();
    const formatDate = (d) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    if (period === 'custom' && customStart && customEnd) return { start: formatDate(customStart), end: formatDate(customEnd) };
    switch (period) {
      case 'today': return { start: formatDate(today), end: formatDate(today) };
      case 'yesterday': const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1); return { start: formatDate(yesterday), end: formatDate(yesterday) };
      case 'last_week': const currentDay = today.getDay(); const daysToLastSunday = currentDay === 0 ? 7 : currentDay; const lastSunday = new Date(today); lastSunday.setDate(today.getDate() - daysToLastSunday); const lastMonday = new Date(lastSunday); lastMonday.setDate(lastSunday.getDate() - 6); return { start: formatDate(lastMonday), end: formatDate(lastSunday) };
      case 'last_month': const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1); const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0); return { start: formatDate(lastMonthStart), end: formatDate(lastMonthEnd) };
      case 'month': default: const monthStart = new Date(today.getFullYear(), today.getMonth(), 1); const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0); return { start: formatDate(monthStart), end: formatDate(monthEnd) };
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const { start, end } = getDateRange();
      let advertiserFilter = selectedAdvertiser !== 'all' ? `&advertiser_id=${selectedAdvertiser}` : '';
      let campaignFilter = selectedCampaign ? `&campaign_id=${selectedCampaign.id}` : '';
      const [statsRes, trendRes, advRes] = await Promise.all([
        axios.get(`/admin/facebook/analytics/campaign-stats?start_date=${start}&end_date=${end}${advertiserFilter}`),
        axios.get(`/admin/facebook/analytics/trends?start_date=${start}&end_date=${end}${advertiserFilter}${campaignFilter}`),
        axios.get('/admin/facebook/advertisers')
      ]);
      setCampaignStats(statsRes.data.data || []);
      setTrend(trendRes.data.lead_trend || []);
      setQualityTrend(trendRes.data.quality_trend || []);
      setCplTrend(trendRes.data.cpl_trend || []);
      setIsHourly(trendRes.data.is_hourly || false);
      setAdvertisers(advRes.data.data || []);
    } catch (error) { console.error('Error fetching analytics:', error); }
    finally { setLoading(false); }
  };

  // Calculate max values for Y axis with padding
  const leadMaxY = useMemo(() => {
    if (!trend.length) return 10;
    const maxTotal = Math.max(...trend.map(t => t.total_leads || 0));
    const maxPositive = Math.max(...trend.map(t => t.positive_leads || 0));
    const max = Math.max(maxTotal, maxPositive);
    return Math.ceil(max * 1.2) || 10;
  }, [trend]);

  const cplMaxY = useMemo(() => {
    if (!cplTrend.length) return 10;
    const max = Math.max(...cplTrend.map(t => t.cpl || 0));
    return Math.ceil(max * 1.2) || 10;
  }, [cplTrend]);

  const handleColumnToggle = (columnId) => setVisibleColumns(prev => prev.includes(columnId) ? prev.filter(id => id !== columnId) : [...prev, columnId]);
  const handleAdvertiserChange = (value) => { setSelectedAdvertiser(value); setSelectedCampaign(null); };
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };

  const PeriodButton = ({ value, label }) => (
    <Box onClick={() => setPeriod(value)} sx={{ px: { xs: 1, sm: 2 }, py: { xs: 0.5, sm: 1 }, borderRadius: 1, cursor: 'pointer', bgcolor: period === value ? 'primary.main' : 'grey.100', color: period === value ? 'white' : 'text.primary', fontWeight: period === value ? 600 : 400, fontSize: { xs: '0.7rem', sm: '0.875rem' }, '&:hover': { bgcolor: period === value ? 'primary.dark' : 'grey.200' } }}>
      {label}
    </Box>
  );

  const campaignsForFilter = selectedAdvertiser !== 'all' ? campaignStats.filter(c => c.advertiser_id === parseInt(selectedAdvertiser)) : campaignStats;

  const filteredCampaigns = campaignStats
    .filter(c => parseInt(c.total_leads) > 0 || parseFloat(c.total_spend) > 0)
    .filter(c => selectedAdvertiser === 'all' || c.advertiser_id === parseInt(selectedAdvertiser))
    .filter(c => !selectedCampaign || c.id === selectedCampaign.id)
    .filter(c => !searchTerm || c.campaign_name?.toLowerCase().includes(searchTerm.toLowerCase()) || c.advertiser_name?.toLowerCase().includes(searchTerm.toLowerCase()));

  const paginatedCampaigns = filteredCampaigns.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const funnelData = useMemo(() => {
    const totals = filteredCampaigns.reduce((acc, c) => ({ impressions: acc.impressions + (parseInt(c.impressions) || 0), clicks: acc.clicks + (parseInt(c.clicks) || 0), leads: acc.leads + (parseInt(c.total_leads) || 0), positive: acc.positive + (parseInt(c.positive_leads) || 0) }), { impressions: 0, clicks: 0, leads: 0, positive: 0 });
    const positivePercent = totals.leads > 0 ? ((totals.positive / totals.leads) * 100).toFixed(1) : 0;
    return { ...totals, positivePercent };
  }, [filteredCampaigns]);

  const tableTotals = useMemo(() => {
    return filteredCampaigns.reduce((acc, c) => {
      const rev = (parseInt(c.positive_leads) || 0) * (parseFloat(c.payout_per_lead) || 0);
      const spend = parseFloat(c.total_spend || 0);
      return {
        total_leads: acc.total_leads + (parseInt(c.total_leads) || 0),
        positive_leads: acc.positive_leads + (parseInt(c.positive_leads) || 0),
        pending_leads: acc.pending_leads + (parseInt(c.pending_leads) || 0),
        trash_leads: acc.trash_leads + (parseInt(c.trash_leads) || 0),
        cpa_leads: acc.cpa_leads + (parseInt(c.cpa_leads) || 0),
        total_revenue: acc.total_revenue + rev,
        total_spend: acc.total_spend + spend,
        profit: acc.profit + (rev - spend),
        impressions: acc.impressions + (parseInt(c.impressions) || 0),
        clicks: acc.clicks + (parseInt(c.clicks) || 0),
      };
    }, { total_leads: 0, positive_leads: 0, pending_leads: 0, trash_leads: 0, cpa_leads: 0, total_revenue: 0, total_spend: 0, profit: 0, impressions: 0, clicks: 0 });
  }, [filteredCampaigns]);

  const displayColumns = isMobile ? ALL_COLUMNS.filter(c => c.mobile && visibleColumns.includes(c.id)) : ALL_COLUMNS.filter(c => visibleColumns.includes(c.id));

  const renderCellValue = (campaign, columnId) => {
    switch (columnId) {
      case 'campaign_name': return <Typography fontWeight={600} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, maxWidth: { xs: 100, sm: 200 }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{campaign.campaign_name}</Typography>;
      case 'advertiser_name': return <Typography sx={{ fontSize: '0.8rem' }}>{campaign.advertiser_name || '-'}</Typography>;
      case 'total_leads': return <Typography fontWeight={600} sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>{campaign.total_leads || 0}</Typography>;
      case 'positive_leads': return <Typography color="success.main" fontWeight={600} sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>{campaign.positive_leads || 0}</Typography>;
      case 'pending_leads': return <Typography color="warning.main" sx={{ fontSize: '0.8rem' }}>{campaign.pending_leads || 0}</Typography>;
      case 'trash_leads': return <Typography color="error.main" sx={{ fontSize: '0.8rem' }}>{campaign.trash_leads || 0}</Typography>;
      case 'cpa_leads': return <Typography sx={{ color: '#2196F3', fontWeight: 600, fontSize: '0.8rem' }}>{campaign.cpa_leads || 0}</Typography>;
      case 'percent_cpa': const pctCpa = campaign.positive_leads > 0 ? Math.round((campaign.cpa_leads / campaign.positive_leads) * 100) : 0; return `${pctCpa}%`;
      case 'percent_positive': const pct = campaign.total_leads > 0 ? Math.round((campaign.positive_leads / campaign.total_leads) * 100) : 0; return <Chip size="small" label={`${pct}%`} sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' }, height: { xs: 18, sm: 22 }, bgcolor: pct >= 50 ? 'success.main' : pct >= 30 ? 'warning.main' : 'error.main', color: 'white' }} />;
      case 'avg_quality_score': return Math.round(campaign.avg_quality_score || 0);
      case 'payout_per_lead': return `€${parseFloat(campaign.payout_per_lead || 0).toFixed(2)}`;
      case 'total_revenue': return <Typography color="success.main" fontWeight={600} sx={{ fontSize: '0.8rem' }}>€{parseFloat(campaign.total_revenue || 0).toFixed(2)}</Typography>;
      case 'total_spend': return <Typography color="error.main" fontWeight={600} sx={{ fontSize: '0.8rem' }}>€{parseFloat(campaign.total_spend || 0).toFixed(2)}</Typography>;
      case 'profit': const revenue = (parseInt(campaign.positive_leads) || 0) * (parseFloat(campaign.payout_per_lead) || 0); const cost = parseFloat(campaign.total_spend || 0); const prof = revenue - cost; return <Typography fontWeight={600} sx={{ fontSize: '0.8rem', color: prof >= 0 ? 'success.main' : 'error.main' }}>€{prof.toFixed(2)}</Typography>;
      case 'impressions': return (campaign.impressions || 0).toLocaleString();
      case 'clicks': return (campaign.clicks || 0).toLocaleString();
      case 'ctr_link': return `${parseFloat(campaign.ctr_link || 0).toFixed(2)}%`;
      case 'frequency': return parseFloat(campaign.frequency || 0).toFixed(2);
      default: return '-';
    }
  };

  const formatXAxis = (value) => {
    if (isHourly) return value;
    const d = new Date(value);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
  };

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress /></Box>;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
      <Box sx={{ width: '100%' }}>
        <Typography variant="h5" fontWeight={700} mb={2} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Facebook Analytics {selectedCampaign && <Chip label={selectedCampaign.campaign_name} size="small" onDelete={() => setSelectedCampaign(null)} sx={{ ml: 1 }} />}
        </Typography>

        {/* Filters Row */}
        <Box display="flex" flexWrap="wrap" alignItems="center" gap={1} mb={3}>
          <PeriodButton value="today" label="Today" />
          <PeriodButton value="yesterday" label="Yest." />
          {!isMobile && <PeriodButton value="last_week" label="Last Week" />}
          <PeriodButton value="month" label="Month" />
          {!isMobile && <PeriodButton value="last_month" label="Last Month" />}
          {!isMobile && <PeriodButton value="custom" label="Custom" />}
          {period === 'custom' && !isMobile && (
            <>
              <DatePicker label="From" value={customStart} onChange={(v) => setCustomStart(v)} slotProps={{ textField: { size: 'small', sx: { width: 130 } } }} />
              <DatePicker label="To" value={customEnd} onChange={(v) => setCustomEnd(v)} slotProps={{ textField: { size: 'small', sx: { width: 130 } } }} />
            </>
          )}
          <Box sx={{ flexGrow: 1 }} />
          {!isMobile && (
            <Autocomplete
              size="small"
              options={campaignsForFilter}
              getOptionLabel={(option) => option?.campaign_name || ''}
              value={selectedCampaign}
              onChange={(e, v) => setSelectedCampaign(v)}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              renderInput={(params) => <TextField {...params} label="Filter by Campaign" placeholder="All Campaigns" />}
              sx={{ minWidth: 250 }}
            />
          )}
        </Box>

        {/* Search and Table Filters */}
        <Box display="flex" flexWrap="wrap" gap={{ xs: 1, sm: 2 }} mb={3} alignItems="center">
          <TextField placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }} InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 20 }} /></InputAdornment> }} />
          {!isMobile && (
            <TextField select label="Advertiser" value={selectedAdvertiser} onChange={(e) => handleAdvertiserChange(e.target.value)} size="small" sx={{ minWidth: 160 }}>
              <MenuItem value="all">All Advertisers</MenuItem>
              {advertisers.map((a) => <MenuItem key={a.advertiser_id} value={a.advertiser_id}>{a.advertiser_name}</MenuItem>)}
            </TextField>
          )}
          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small"><MoreVert /></IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
            <MenuItem disabled><Typography variant="subtitle2">Show/Hide Columns</Typography></MenuItem>
            {ALL_COLUMNS.map((col) => <MenuItem key={col.id} onClick={() => handleColumnToggle(col.id)}><Checkbox checked={visibleColumns.includes(col.id)} size="small" /><ListItemText primary={col.label} /></MenuItem>)}
          </Menu>
        </Box>

        {/* Campaigns Table */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 0 }}>
            <TableContainer sx={{ maxHeight: { xs: 350, sm: 450 } }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {displayColumns.map((col) => <TableCell key={col.id} align={col.id === 'campaign_name' || col.id === 'advertiser_name' ? 'left' : 'center'} sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>{col.label}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedCampaigns.length === 0 ? (
                    <TableRow><TableCell colSpan={displayColumns.length} align="center"><Typography color="text.secondary" py={4}>No campaigns with activity</Typography></TableCell></TableRow>
                  ) : paginatedCampaigns.map((campaign) => (
                    <TableRow key={campaign.id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelectedCampaign(campaign)}>
                      {displayColumns.map((col) => <TableCell key={col.id} align={col.id === 'campaign_name' || col.id === 'advertiser_name' ? 'left' : 'center'}>{renderCellValue(campaign, col.id)}</TableCell>)}
                    </TableRow>
                  ))}
                </TableBody>
               {filteredCampaigns.length > 0 && (
                <TableBody>
                  <TableRow sx={{ bgcolor: '#f0f4ff', '& td': { fontWeight: 700, borderTop: '2px solid #3b82f6' } }}>
                    {displayColumns.map((col) => (
                      <TableCell key={col.id} align={col.id === 'campaign_name' || col.id === 'advertiser_name' ? 'left' : 'center'} sx={{ fontSize: '0.8rem' }}>
                        {col.id === 'campaign_name' ? 'TOTALS' : 
                         col.id === 'advertiser_name' ? '' :
                         col.id === 'total_leads' ? tableTotals.total_leads :
                         col.id === 'positive_leads' ? <Typography color="success.main" fontWeight={700} sx={{ fontSize: '0.8rem' }}>{tableTotals.positive_leads}</Typography> :
                         col.id === 'pending_leads' ? tableTotals.pending_leads :
                         col.id === 'trash_leads' ? tableTotals.trash_leads :
                         col.id === 'cpa_leads' ? tableTotals.cpa_leads :
                         col.id === 'percent_positive' ? (tableTotals.total_leads > 0 ? Math.round((tableTotals.positive_leads / tableTotals.total_leads) * 100) + '%' : '0%') :
                         col.id === 'percent_cpa' ? (tableTotals.positive_leads > 0 ? Math.round((tableTotals.cpa_leads / tableTotals.positive_leads) * 100) + '%' : '0%') :
                         col.id === 'avg_quality_score' ? '' :
                         col.id === 'payout_per_lead' ? '' :
                         col.id === 'total_revenue' ? <Typography color="success.main" fontWeight={700} sx={{ fontSize: '0.8rem' }}>€{tableTotals.total_revenue.toFixed(2)}</Typography> :
                         col.id === 'total_spend' ? <Typography color="error.main" fontWeight={700} sx={{ fontSize: '0.8rem' }}>€{tableTotals.total_spend.toFixed(2)}</Typography> :
                         col.id === 'profit' ? <Typography fontWeight={700} sx={{ fontSize: '0.8rem', color: tableTotals.profit >= 0 ? 'success.main' : 'error.main' }}>€{tableTotals.profit.toFixed(2)}</Typography> :
                         col.id === 'impressions' ? tableTotals.impressions.toLocaleString() :
                         col.id === 'clicks' ? tableTotals.clicks.toLocaleString() :
                         ''}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
                )}
              </Table>
            </TableContainer>
            <TablePagination component="div" count={filteredCampaigns.length} page={page} onPageChange={handleChangePage} rowsPerPage={rowsPerPage} onRowsPerPageChange={handleChangeRowsPerPage} rowsPerPageOptions={[10, 25, 50]} labelRowsPerPage={isMobile ? '' : 'Rows:'} />
          </CardContent>
        </Card>

        {/* Charts Row 1 */}
        <Box display="grid" gridTemplateColumns={{ xs: '1fr', lg: '1fr 1fr' }} gap={{ xs: 2, sm: 3 }} mb={3}>
          {/* Lead Trend */}
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography variant="h6" fontWeight={600} mb={2} sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>Lead Trend</Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 200 : 280}>
                <AreaChart data={trend}>
                  <defs>
                    <linearGradient id="gradientLeadTotal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradientLeadPositive" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey={isHourly ? "label" : "date"} tickFormatter={(v) => isHourly ? v : formatXAxis(v)} stroke="#9ca3af" style={{ fontSize: isMobile ? '0.6rem' : '0.7rem' }} tickLine={false} interval={isMobile ? 4 : 2} />
                  <YAxis domain={[0, leadMaxY]} stroke="#9ca3af" style={{ fontSize: '0.7rem' }} tickLine={false} axisLine={false} hide={isMobile} />
                  <Tooltip labelFormatter={(v) => new Date(v).toLocaleDateString('it-IT')} />
                  <Area type="monotone" dataKey="total_leads" stroke="#3b82f6" strokeWidth={2} fill="url(#gradientLeadTotal)" name="Total" />
                  <Area type="monotone" dataKey="positive_leads" stroke="#10b981" strokeWidth={2} fill="url(#gradientLeadPositive)" name="Positive" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Quality Score Trend */}
          {!isMobile && (
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography variant="h6" fontWeight={600} mb={2} sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>Quality Score Trend</Typography>
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={qualityTrend}>
                    <defs>
                      <linearGradient id="gradientQuality" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey={isHourly ? "label" : "date"} tickFormatter={(v) => isHourly ? v : formatXAxis(v)} stroke="#9ca3af" style={{ fontSize: '0.7rem' }} tickLine={false} interval={2} />
                    <YAxis domain={[0, 100]} stroke="#9ca3af" style={{ fontSize: '0.7rem' }} tickLine={false} axisLine={false} />
                    <Tooltip labelFormatter={(v) => new Date(v).toLocaleDateString('it-IT')} formatter={(v) => [Math.round(v), 'Quality']} />
                    <Area type="monotone" dataKey="avg_quality" stroke="#8b5cf6" strokeWidth={2} fill="url(#gradientQuality)" name="Quality" />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </Box>

        {/* Charts Row 2 */}
        {!isTablet && (
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={3}>
             {/* CPL Trend */}
<Card>
  <CardContent sx={{ p: 2 }}>
    <Typography variant="h6" fontWeight={600} mb={2} sx={{ fontSize: '1.1rem' }}>Cost Per Lead Trend</Typography>
    {isHourly ? (
      <Box display="flex" justifyContent="center" alignItems="center" height={280}>
        <Typography color="text.secondary">CPL trend available for multi-day periods</Typography>
      </Box>
    ) : (
      <ResponsiveContainer width="100%" height={280}>
        <AreaChart data={cplTrend}>
          <defs>
            <linearGradient id="gradientCPL" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.4} />
              <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey="date" tickFormatter={formatXAxis} stroke="#9ca3af" style={{ fontSize: '0.7rem' }} tickLine={false} interval={2} />
          <YAxis domain={[0, cplMaxY]} tickFormatter={(v) => `€${v}`} stroke="#9ca3af" style={{ fontSize: '0.7rem' }} tickLine={false} axisLine={false} />
          <Tooltip labelFormatter={(v) => new Date(v).toLocaleDateString('it-IT')} formatter={(v) => [`€${parseFloat(v).toFixed(2)}`, 'CPL']} />
          <Area type="monotone" dataKey="cpl" stroke="#f59e0b" strokeWidth={2} fill="url(#gradientCPL)" name="CPL" />
        </AreaChart>
      </ResponsiveContainer>
    )}
  </CardContent>
</Card>

            {/* Conversion Funnel */}
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="h6" fontWeight={600} mb={2} sx={{ fontSize: '1.1rem' }}>Conversion Funnel</Typography>
                <Box display="flex" justifyContent="space-around" alignItems="flex-end" height={260} pt={2}>
                  {[
                    { name: 'Impr.', value: funnelData.impressions, fill: '#3B82F6', nextValue: funnelData.clicks },
                    { name: 'Clicks', value: funnelData.clicks, fill: '#8B5CF6', nextValue: funnelData.leads },
                    { name: 'Leads', value: funnelData.leads, fill: '#F59E0B', nextValue: funnelData.positive },
                    { name: 'Positive', value: funnelData.positive, fill: '#10B981', nextValue: null }
                  ].map((item) => {
                    const maxValue = Math.max(funnelData.impressions, funnelData.clicks, funnelData.leads, funnelData.positive, 1);
                    const heightPercent = (item.value / maxValue) * 100;
                    const conversionRate = item.nextValue !== null && item.value > 0 ? ((item.nextValue / item.value) * 100).toFixed(1) : null;
                    const positiveRate = item.name === 'Positive' ? funnelData.positivePercent : null;
                    return (
                      <Box key={item.name} textAlign="center" flex={1} mx={0.5} height="100%" display="flex" flexDirection="column" justifyContent="flex-end">
                        <Typography variant="body1" fontWeight={700} mb={1}>{item.value.toLocaleString()}</Typography>
                        <Box sx={{ height: `${Math.max(heightPercent, 8)}%`, minHeight: 30, bgcolor: item.fill, borderRadius: '6px 6px 0 0', mb: 1 }} />
                        <Typography variant="body2" fontWeight={500}>{item.name}</Typography>
                        {conversionRate && <Typography variant="caption" color="text.secondary">→ {conversionRate}%</Typography>}
                        {positiveRate && <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 600 }}>{positiveRate}%</Typography>}
                      </Box>
                    );
                  })}
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
}

export default FbAnalytics;
